import {
  INSURANCE_CARD_SIDES,
  MODE_APPOINTMENT,
  MODE_CONTACT,
} from 'client-portal/utils/prescreener/constants';
import { TrackedMap } from 'tracked-built-ins';
import { bannerError } from 'ember-simplepractice/utils/error-handling';
import { cached, tracked } from '@glimmer/tracking';
import { didCancel } from 'ember-concurrency';
import { waitForRender } from 'ember-simplepractice/utils/waiters';
import Service, { inject as service } from '@ember/service';

export default class PrescreenerIndexService extends Service {
  @service('prescreener/validation') validation;
  @service('prescreener/preview') preview;
  @service('prescreener/step') step;
  @service currentPractice;
  @service session;
  @service store;

  @tracked questionRecord;

  insuranceCards = new TrackedMap();
  answerRecord;

  get hasNavCategories() {
    return !!this.preview.previewConfig?.categories?.length || !!this.step.categories?.length;
  }

  get questions() {
    return this.questionRecord?.questions;
  }

  get isContactFormEnabled() {
    return this.canShowPrescreener && this.#shouldShowFor(MODE_CONTACT);
  }

  get isAppointmentFormEnabled() {
    return this.canShowPrescreener && this.#shouldShowFor(MODE_APPOINTMENT);
  }

  get canShowPrescreener() {
    const isEnabled = this.hasPrescreenerQuestions && !this.isNewClient;
    const canShow = !!this.preview.isPreview || isEnabled;
    return this.featurePrescreenerV2 && canShow;
  }

  @cached
  get featurePrescreenerV2() {
    return (
      this.currentPractice.get('featurePrescreenerV2') &&
      this.session.isClientPortalMonarchOrSpWebsite
    );
  }

  setPreviewConfig(value) {
    this.preview.previewConfig = value;
  }

  get insuranceCardFront() {
    return this.insuranceCards.get(INSURANCE_CARD_SIDES.FRONT_SIDE);
  }

  set insuranceCardFront(file) {
    this.insuranceCards.set(INSURANCE_CARD_SIDES.FRONT_SIDE, file);
  }

  get insuranceCardBack() {
    return this.insuranceCards.get(INSURANCE_CARD_SIDES.BACK_SIDE);
  }

  set insuranceCardBack(file) {
    this.insuranceCards.set(INSURANCE_CARD_SIDES.BACK_SIDE, file);
  }

  get hasPrescreenerQuestions() {
    return !!this.questionRecord?.questions?.length;
  }

  async uploadCard(clientId, file) {
    let unauthenticatedUpload = this.store.createRecord('unauthenticated-insurance-card', {
      file,
      clientId,
    });
    await unauthenticatedUpload.save();
  }

  async uploadInsuranceDocuments(clientId) {
    let cards = Array.from(this.insuranceCards.values());
    return await Promise.allSettled(
      cards.map(async card => {
        await this.uploadCard(clientId, card);
      })
    );
  }

  getAnswerDataForClient() {
    return {
      prescreenerAnswer: this.answerRecord,
      insuranceCardFrontSideFile: this.insuranceCardFront,
      insuranceCardBackSideFile: this.insuranceCardBack,
    };
  }

  reset() {
    this.files = new TrackedMap();
    this.questionRecord = null;
    this.validation.clearValidationModels();
  }

  updateAndValidateAnswer(questionData, answer) {
    this.validation.updateAnswerModel(questionData, answer);
    this.answerRecord.answers = this.validation.answers;
  }

  async updatePrescreenerModels() {
    if (!this.featurePrescreenerV2) return;

    try {
      await this.#getQuestionAndAnswerModels();
    } catch (e) {
      this.handlePrescreenerLoadError(e);
    }
  }

  async #getQuestionAndAnswerModels() {
    this.questionRecord = await this.#getPrescreenerModel();
    this.answerRecord = this.#getPrescreenerAnswerModel(this.questionRecord);
    this.step.updateCategories();
  }

  async handlePrescreenerLoadError(e) {
    if (!didCancel(e)) {
      await waitForRender();
      bannerError(e, { title: 'Prescreener questions could not be loaded' });
    }
  }

  #shouldShowFor(formType) {
    let { showOnContactForm, showOnAppointmentForm } = this.questionRecord ?? {};
    return (
      (showOnContactForm && formType === MODE_CONTACT) ||
      (showOnAppointmentForm && formType === MODE_APPOINTMENT)
    );
  }

  async #getPrescreenerModel() {
    if (this.preview.isPreview) {
      return this.preview.getPrescreenerPreview();
    } else {
      return this.#queryStoreForPrescreenerQuestions();
    }
  }

  async #queryStoreForPrescreenerQuestions() {
    let loadedQuestions = this.store.peekAll('prescreener-question').firstObject;

    return (
      loadedQuestions ??
      (
        await this.store.query('prescreener-question', {
          'practice_id': this.currentPractice.content.id,
        })
      ).firstObject
    );
  }

  #getPrescreenerAnswerModel(prescreenerQuestion) {
    return this.store.createRecord('prescreener-answer', {
      prescreenerQuestion,
    });
  }
}
